import React, { useState, useEffect, useCallback } from 'react';
import { notificationsAPI } from '../lib/api';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bell, 
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  Search,
  Filter,
  MessageSquare,
  Briefcase,
  CheckSquare,
  AlertCircle,
  Calendar,
  Receipt,
  X,
  Loader2,
  Settings,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

const NOTIFICATION_TYPES = {
  all: { label: 'All', icon: Bell, color: 'text-slate-500' },
  message: { label: 'Messages', icon: MessageSquare, color: 'text-blue-500' },
  task: { label: 'Tasks', icon: CheckSquare, color: 'text-green-500' },
  case_update: { label: 'Cases', icon: Briefcase, color: 'text-amber-500' },
  calendar: { label: 'Calendar', icon: Calendar, color: 'text-purple-500' },
  invoice: { label: 'Invoices', icon: Receipt, color: 'text-emerald-500' },
  system: { label: 'System', icon: AlertCircle, color: 'text-red-500' },
};

const getNotificationIcon = (type) => {
  const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.system;
  const Icon = config.icon;
  return <Icon className={`h-4 w-4 ${config.color}`} />;
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { markAllAsRead, clearNotifications } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [countsByType, setCountsByType] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 100,
        unread_only: showUnreadOnly,
      };
      if (activeTab !== 'all') {
        params.notification_type = activeTab;
      }
      
      const res = await notificationsAPI.getGrouped(params);
      setNotifications(res.data.notifications || []);
      setCountsByType(res.data.counts_by_type || {});
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, showUnreadOnly]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await notificationsAPI.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications?')) return;
    
    try {
      await notificationsAPI.clearAll();
      setNotifications([]);
      clearNotifications();
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      handleMarkRead(notification.notification_id);
    }
    
    // Navigate to related content
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.notification_type === 'case_update' && notification.resource_id) {
      navigate(`/cases/${notification.resource_id}`);
    } else if (notification.notification_type === 'task' && notification.resource_id) {
      navigate(`/tasks`);
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        n.title?.toLowerCase().includes(search) ||
        n.message?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const totalCount = notifications.length;

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="notification-center-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications
          </h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'} • {totalCount} total
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchNotifications}
            className="gap-1"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <Filter className="h-4 w-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleMarkAllRead}>
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark All as Read
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              >
                {showUnreadOnly ? (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    Show All
                  </>
                ) : (
                  <>
                    <BellOff className="h-4 w-4 mr-2" />
                    Show Unread Only
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleClearAll}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Link to="/settings/notifications">
            <Button variant="outline" size="sm" className="gap-1">
              <Settings className="h-4 w-4" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search notifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="notification-search"
        />
      </div>

      {/* Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          {Object.entries(NOTIFICATION_TYPES).map(([key, config]) => {
            const Icon = config.icon;
            const count = key === 'all' 
              ? totalCount 
              : (countsByType[key] || 0);
            
            return (
              <TabsTrigger 
                key={key} 
                value={key}
                className="gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              >
                <Icon className="h-4 w-4" />
                {config.label}
                {count > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Notification List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Bell className="h-12 w-12 mb-3 opacity-30" />
              <p className="font-medium">No notifications</p>
              <p className="text-sm">
                {searchQuery ? 'Try a different search' : 'You\'re all caught up!'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="divide-y">
                {filteredNotifications.map(notification => (
                  <div
                    key={notification.notification_id}
                    className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        !notification.read ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`font-medium truncate ${
                            !notification.read ? 'text-slate-900' : 'text-slate-600'
                          }`}>
                            {notification.title}
                          </p>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {NOTIFICATION_TYPES[notification.notification_type]?.label || 'System'}
                          </Badge>
                          {!notification.read && (
                            <Badge className="bg-blue-500 text-xs">New</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkRead(notification.notification_id);
                            }}
                            title="Mark as read"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss(notification.notification_id);
                          }}
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(NOTIFICATION_TYPES).slice(1).map(([key, config]) => {
          const Icon = config.icon;
          const count = countsByType[key] || 0;
          
          return (
            <Card 
              key={key}
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => setActiveTab(key)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-slate-100`}>
                  <Icon className={`h-5 w-5 ${config.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-gray-500">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
